/**
 * Meeting (ahoy_meeting) CRM trigger card variant — Header, collapsed
 * PreviewBody, and expanded Body.
 */

import { Calendar, Users01 } from '@untitledui/icons';

import type { CrmEventTriggerData } from '@/lib/proposals/core/types';
import { type TimeFormat, useOrgTimeFormat } from '@/lib/time';
import { humanize } from '@/utils/text';

import { formatMeetingTime, getStringValue } from './helpers';

const MEETING_STATUS_LABELS: Record<string, string> = {
  tentative: 'Tentative',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
};

/**
 * Helper that pulls the meeting fields out of a trigger so the Header
 * and Body components share extraction logic.
 */
function getMeetingData(data: CrmEventTriggerData, timeFormat: TimeFormat) {
  const { event, display_name } = data.crm_event;
  const title = getStringValue(event, 'ahoy_title') || display_name;
  const startTime = getStringValue(event, 'ahoy_start_time');
  const location = getStringValue(event, 'ahoy_location');
  const status = getStringValue(event, 'ahoy_status');
  const invitations = event.ahoy_invitations;
  const attendees = Array.isArray(invitations)
    ? invitations
        .map(inv => {
          const obj = inv as Record<string, unknown>;
          return (obj.invitee_name || obj.email || '') as string;
        })
        .filter(Boolean)
    : [];
  const meetingTime = startTime
    ? formatMeetingTime(startTime, timeFormat)
    : null;
  return { title, status, location, attendees, meetingTime };
}

export function MeetingHeader({ data }: { data: CrmEventTriggerData }) {
  // Org default read from context — not threaded through props.
  const { timeFormat } = useOrgTimeFormat();
  const { title, status } = getMeetingData(data, timeFormat);
  return (
    <div className='flex-1 min-w-0'>
      <div className='flex items-center gap-2'>
        <span className='text-sm font-medium text-primary truncate'>
          {title}
        </span>
        {status && (
          <>
            <span className='text-tertiary flex-shrink-0'>·</span>
            <span className='text-xs text-tertiary flex-shrink-0'>
              {MEETING_STATUS_LABELS[status] ?? humanize(status)}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/** Compact meeting preview: time + attendees + location in icon form. */
export function MeetingPreviewBody({ data }: { data: CrmEventTriggerData }) {
  // Org default read from context — not threaded through props.
  const { timeFormat } = useOrgTimeFormat();
  const { meetingTime, attendees, location } = getMeetingData(data, timeFormat);
  if (!meetingTime && attendees.length === 0 && !location) {
    return null;
  }
  return (
    <div className='space-y-1'>
      {(meetingTime || attendees.length > 0) && (
        <div className='text-xs text-secondary flex items-center gap-2 flex-wrap'>
          {meetingTime && (
            <span className='flex items-center gap-1'>
              <Calendar className='w-3 h-3' />
              {meetingTime}
            </span>
          )}
          {attendees.length > 0 && (
            <span className='flex items-center gap-1'>
              <Users01 className='w-3 h-3' />
              {attendees.slice(0, 3).join(', ')}
              {attendees.length > 3 && ` +${attendees.length - 3}`}
            </span>
          )}
        </div>
      )}
      {location && (
        <div className='text-xs text-tertiary line-clamp-1'>{location}</div>
      )}
    </div>
  );
}

export function MeetingBody({ data }: { data: CrmEventTriggerData }) {
  // Org default read from context — not threaded through props.
  const { timeFormat } = useOrgTimeFormat();
  const { meetingTime, location, attendees } = getMeetingData(data, timeFormat);
  if (!meetingTime && !location && attendees.length === 0) {
    return null;
  }
  return (
    <div className='space-y-3'>
      <div>
        <div className='text-xs font-medium text-secondary mb-1'>
          Meeting details
        </div>
        <div className='text-xs text-secondary space-y-1'>
          {meetingTime && (
            <div className='flex items-center gap-1.5'>
              <Calendar className='w-3.5 h-3.5 flex-shrink-0' />
              <span>{meetingTime}</span>
            </div>
          )}
          {location && (
            <div className='flex items-start gap-1.5'>
              <span className='text-tertiary'>📍</span>
              <span>{location}</span>
            </div>
          )}
          {attendees.length > 0 && (
            <div className='flex items-start gap-1.5'>
              <Users01 className='w-3.5 h-3.5 flex-shrink-0 mt-0.5' />
              <span>{attendees.join(', ')}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
